/**
 * data/treatments.ts — the public treatment menu, taken verbatim from
 * Grace Haven's price list. Prices are in Botswana Pula (BWP).
 * In Phase 2 these become editable from the admin portal (Firestore
 * `treatments`); this file is the seed / fallback.
 */
export interface Treatment {
  id: string;
  name: string;
  price: number; // BWP
  durationMin?: number;
  note?: string;
  description?: string;
}

export interface TreatmentCategory {
  id: string;
  title: string;
  blurb: string;
  treatments: Treatment[];
}

export const TREATMENT_MENU: TreatmentCategory[] = [
  {
    id: 'massage',
    title: 'Massage',
    blurb: 'Skilled hands and warmth to unwind the body and quiet the mind.',
    treatments: [
      { id: 'massage-1hr', name: 'Full Body Massage (1hr)', price: 300, durationMin: 60, description: 'A soothing full-body massage to ease everyday tension and leave you completely unwound.' },
      { id: 'swedish', name: 'Swedish Massage', price: 300, durationMin: 60, description: 'Long, flowing strokes that relax muscles, improve circulation, and calm the mind — our classic.' },
      { id: 'deep-tissue', name: 'Deep Tissue', price: 350, durationMin: 60, description: 'Firmer, focused pressure that reaches deeper muscle layers to release stubborn knots and tightness.' },
      { id: 'hot-stone', name: 'Hot Stone', price: 350, durationMin: 60, description: 'Warm basalt stones melt away tension and deeply relax the body — wonderfully grounding.' },
      { id: 'aromatherapy', name: 'Aromatherapy', price: 400, durationMin: 75, description: 'A gentle massage with botanical essential oils chosen to relax, uplift, or restore your balance.' },
      { id: 'lymphatic', name: 'Lymphatic Drainage', price: 500, durationMin: 75, description: 'Light, rhythmic strokes that encourage lymph flow, reduce puffiness, and leave you feeling lighter.' },
    ],
  },
  {
    id: 'facials',
    title: 'Facials',
    blurb: 'Considered skincare for a fresh, well-rested complexion.',
    treatments: [
      { id: 'acne-attack', name: 'Acne Attack', price: 400, durationMin: 60, description: 'A targeted facial to deep-cleanse, calm breakouts, and help clear congested, acne-prone skin.' },
      { id: 'derma-planning', name: 'Derma Planning', price: 350, durationMin: 45, description: 'Gentle exfoliation that removes dead skin and fine vellus hair for a smooth, radiant finish.' },
      { id: 'glow-facial', name: 'Glow Facial', price: 300, durationMin: 45, description: 'A brightening facial that hydrates and revives dull skin for a fresh, healthy glow.' },
      { id: 'deep-cleansing', name: 'Deep Cleansing', price: 250, durationMin: 45, description: 'A thorough cleanse, exfoliation, and extraction to leave skin clear, balanced, and refreshed.' },
      { id: 'skin-tag', name: 'Skin Tag Removal', price: 150, note: 'from', description: 'Safe, careful removal of skin tags by our trained therapists. Priced from, depending on number and size.' },
    ],
  },
  {
    id: 'nails',
    title: 'Nails',
    blurb: 'Polished, lasting finishes for hands and feet.',
    treatments: [
      { id: 'gel-overlay', name: 'Gel Overlay', price: 110 },
      { id: 'gel-on-stick', name: 'Gel on Stick', price: 130 },
      { id: 'gel-on-tips', name: 'Gel on Tips', price: 150 },
      { id: 'buff-shine', name: 'Buff & Shine', price: 100 },
      { id: 'pedicure', name: 'Pedicure', price: 110 },
    ],
  },
  {
    id: 'combo',
    title: 'Combos',
    blurb: 'Favourite pairings, priced together.',
    treatments: [
      { id: 'gel-naturals-pedi', name: 'Gel on Naturals + Pedi', price: 180 },
      { id: 'gel-tips-pedi', name: 'Gel on Tips + Pedi', price: 240 },
      { id: 'buff-shine-pedi', name: 'Buff & Shine + Pedi', price: 180 },
      { id: 'gel-lashes', name: 'Gel Nails + Lashes', price: 230 },
    ],
  },
  {
    id: 'makeup',
    title: 'Makeup',
    blurb: 'Finishing touches for any occasion.',
    treatments: [
      { id: 'soft-glam', name: 'Natural / Soft Glam', price: 250 },
      { id: 'full-glam', name: 'Full Glam', price: 300 },
    ],
  },
  {
    id: 'addons',
    title: 'Add-ons',
    blurb: 'Little extras to complete your treatment.',
    treatments: [
      { id: 'lashes', name: 'Lashes', price: 250 },
      { id: 'soak-off', name: 'Soak Off', price: 120 },
      { id: 'extra-length', name: 'Extra Length', price: 40 },
      { id: 'french-tips', name: 'French Tips', price: 30 },
      { id: 'nail-art', name: 'Nail Art (per nail)', price: 10 },
    ],
  },
];

/** Winter specials — a separate, time-limited list from the flyer. */
export const WINTER_SPECIALS: Treatment[] = [
  { id: 'sp-deep-wax', name: 'Full body deep-tissue massage + underarm wax', price: 400 },
  { id: 'sp-half-gel', name: 'Half body massage + gel overlay (hands & toes)', price: 400 },
  { id: 'sp-glam-brow', name: 'Full glam + eyebrow wax', price: 300 },
  { id: 'sp-hollywood', name: 'Hollywood + underarm wax', price: 300 },
  { id: 'sp-hotstone-facial', name: 'Hot stone back massage + deep cleansing facial', price: 350 },
  { id: 'sp-facial-foot', name: 'Facial + foot massage', price: 250 },
  { id: 'sp-massage-facials', name: 'Full body massage + facials', price: 400 },
];

export const FEATURED_IDS = ['aromatherapy', 'hot-stone', 'glow-facial', 'sp-massage-facials'];

export const ALL_TREATMENTS: Treatment[] = [
  ...TREATMENT_MENU.flatMap((c) => c.treatments),
  ...WINTER_SPECIALS,
];
